import { lazy, Suspense, useEffect, useState } from 'react';
import { AnyRouter } from '@tanstack/react-router';
import { useHotkey } from '@tanstack/react-hotkeys';

// Dynamic import, not a static one: `DevTools` pulls in
// @tanstack/react-devtools + react-router-devtools + react-query-devtools +
// react-hotkeys-devtools. Kept out of every route's module graph so it's
// never fetched -- in prod or dev -- unless something actually asks for it.
// Vite doesn't preload/prefetch a bare `import()` target, so this chunk gets
// the browser's ordinary low-priority lazy-fetch treatment by default; no
// extra `fetchpriority` plumbing needed.
const DevTools = lazy(() =>
	import('./dev-tools').then((m) => ({ default: m.DevTools })),
);

const isDev = import.meta.env.MODE === 'development';

declare global {
	interface Window {
		/** Opens the TanStack devtools panel (Query + Router + Hotkeys) on
		 * demand, in any build including production -- run `toggleDevTools()`
		 * in the browser console. The panel code itself is a separate chunk
		 * (see `DevTools` above) that's only fetched the first time this
		 * fires. */
		toggleDevTools?: () => void;
	}
}

/**
 * Single mount point for the devtools panel, shared by every template's root
 * route. Visible by default in dev; in prod it renders nothing -- and loads
 * nothing -- until `window.toggleDevTools()` is called.
 */
export function DevToolsLoader({ router }: { router?: AnyRouter }) {
	const [visible, setVisible] = useState(isDev);

	const toggle = () => setVisible((old) => !old);

	useEffect(() => {
		window.toggleDevTools = toggle;
		return () => {
			delete window.toggleDevTools;
		};
	}, []);

	// Registered here, not inside `DevTools`, so the hotkey keeps working
	// after the panel is closed -- `DevTools` unmounts entirely when
	// `visible` is false, taking any hotkey registered inside it down too.
	useHotkey('Shift+D', toggle);

	if (!visible) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<DevTools router={router} />
		</Suspense>
	);
}
